import React from 'react';
import { Box, Card, CardActionArea, CardContent, Skeleton, Typography } from '@mui/material';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import SlideshowOutlinedIcon from '@mui/icons-material/SlideshowOutlined';
import CollectionsOutlinedIcon from '@mui/icons-material/CollectionsOutlined';

const formatMetric = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatPostedOn = (media) => {
  const rawDate = media?.posted_at || media?.timestamp || media?.date || media?.snapshot_date;
  if (!rawDate) return '';

  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return '';

  return `Posted on ${new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(parsed)}`;
};

const MEDIA_TYPE_META = {
  IMAGE: { label: 'Post', Icon: ImageOutlinedIcon },
  VIDEO: { label: 'Reel', Icon: SlideshowOutlinedIcon },
  REELS: { label: 'Reel', Icon: SlideshowOutlinedIcon },
  CAROUSEL_ALBUM: { label: 'Carousel', Icon: CollectionsOutlinedIcon },
};

const resolveMediaType = (media) =>
  MEDIA_TYPE_META[String(media?.media_type || '').toUpperCase()] || MEDIA_TYPE_META.IMAGE;

const metricItems = (media) => [
  { key: 'likes', label: 'Likes', Icon: FavoriteBorderIcon, value: formatMetric(media?.likes) },
  { key: 'comments', label: 'Comments', Icon: ChatBubbleOutlineIcon, value: formatMetric(media?.comments) },
  { key: 'shares', label: 'Shares', Icon: SendOutlinedIcon, value: formatMetric(media?.shares) },
  { key: 'saves', label: 'Saves', Icon: BookmarkBorderIcon, value: formatMetric(media?.saves) },
  { key: 'views', label: 'Views', Icon: VisibilityOutlinedIcon, value: formatMetric(media?.views) },
  { key: 'reach', label: 'Reach', Icon: TrendingUpIcon, value: formatMetric(media?.reach) },
];

const MediaCard = ({ media, loading, onClick }) => {
  if (loading) {
    return (
      <Card className="profile-media-card">
        <Skeleton
          variant="rectangular"
          className="profile-media-card-image"
          sx={{ width: '100%', aspectRatio: '4 / 5', height: 'auto' }}
        />
        <CardContent className="profile-media-card-content">
          <Skeleton width="55%" />
          <Skeleton width="100%" height={24} />
        </CardContent>
      </Card>
    );
  }

  const postedOn = formatPostedOn(media);
  const { label: typeLabel, Icon: TypeIcon } = resolveMediaType(media);

  return (
    <Card className="profile-media-card">
      <CardActionArea onClick={() => onClick(media)}>
        <Box className="profile-media-card-media">
          <Box
            component="img"
            src={media?.thumbnail_url || media?.media_url}
            alt={media?.caption || 'Media analytics preview'}
            className="profile-media-card-image"
          />
          <Box className="profile-media-card-type">
            <TypeIcon fontSize="inherit" />
            <span>{typeLabel}</span>
          </Box>
        </Box>

        <CardContent className="profile-media-card-content">
          {postedOn ? (
            <Typography variant="body2" className="profile-media-card-date">
              {postedOn}
            </Typography>
          ) : null}

          <Box className="profile-media-card-metrics">
            {metricItems(media).map((item) => (
              <Box key={item.key} className="profile-media-card-metric" title={item.label}>
                <item.Icon fontSize="inherit" className="profile-media-card-metric-icon" />
                <span className="profile-media-card-metric-value">{item.value}</span>
              </Box>
            ))}
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

export default MediaCard;
